import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import configFile from '../config.js';

const AgentConfigContext = createContext();

export const useAgentConfig = () => {
  const context = useContext(AgentConfigContext);
  if (!context) {
    throw new Error('useAgentConfig must be used within an AgentConfigProvider');
  }
  return context;
};

export const AgentConfigProvider = ({ children }) => {
  const [selectedAgentIndex, setSelectedAgentIndex] = useState(0);
  const [availableAgents, setAvailableAgents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [config, setConfig] = useState({
    agentId: '',
    name: 'Loading...',
    serviceUrl: configFile.serviceUrl,
    apiKey: configFile.apiKey,
  });
  // Security params filled by the user (Postman-style key-value pairs for internal testing).
  // Persisted to localStorage so they survive page reloads.
  const [securityParams, setSecurityParamsState] = useState(() => {
    try {
      const stored = localStorage.getItem('securityParams');
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  const setSecurityParams = (params) => {
    setSecurityParamsState(params);
    try {
      localStorage.setItem('securityParams', JSON.stringify(params || {}));
    } catch {
      // Ignore storage errors
    }
  };

  // Fetch agents from API
  useEffect(() => {
    const fetchAgents = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const response = await axios.get(configFile.canvasApiUrl, {
          headers: {
            'Authorization': `Bearer ${configFile.apiKey}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.data && response.data.code === 0 && response.data.data && response.data.data.length > 0) {
          const agents = response.data.data.map(agent => ({
            agentId: agent.id,
            name: agent.name,
            serviceUrl: configFile.serviceUrl,
            apiKey: configFile.apiKey,
          }));

          setAvailableAgents(agents);

          // Load saved agent index from localStorage
          const savedAgentIndex = localStorage.getItem('selectedAgentIndex');
          let indexToSelect = 0;

          if (savedAgentIndex !== null) {
            const index = parseInt(savedAgentIndex, 10);
            if (index >= 0 && index < agents.length) {
              indexToSelect = index;
            }
          }

          setSelectedAgentIndex(indexToSelect);
          setConfig(agents[indexToSelect]);
        } else {
          throw new Error('No agents found or invalid response format');
        }
      } catch (err) {
        console.error('Error fetching agents:', err);
        setError(err.message || 'Failed to fetch agents');
        const fallbackAgent = {
          agentId: '',
          name: 'Error loading agents',
          serviceUrl: configFile.serviceUrl,
          apiKey: configFile.apiKey,
        };
        setAvailableAgents([fallbackAgent]);
        setConfig(fallbackAgent);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAgents();
  }, []);

  const selectAgent = (index) => {
    if (index >= 0 && index < availableAgents.length) {
      setSelectedAgentIndex(index);
      setConfig(availableAgents[index]);
      localStorage.setItem('selectedAgentIndex', index.toString());
      // Reset security params when switching agents — each agent has its own
      // required params and stale values from the previous agent should not carry over.
      setSecurityParams({});
    }
  };

  const value = {
    config,
    selectedAgentIndex,
    selectAgent,
    availableAgents,
    isLoading,
    error,
    securityParams,
    setSecurityParams,
  };

  return (
    <AgentConfigContext.Provider value={value}>
      {children}
    </AgentConfigContext.Provider>
  );
};